import axios from "axios";
import bundled from "./contractData.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TABLE_NAMES = [
  "certification_runs", "pipeline_stage_runs", "source_objects", "report_instances", "statement_instances",
  "source_rows", "source_cells", "header_period_evidence", "structural_eligibility", "source_atoms",
  "lineage_edges", "semantic_comparison_results", "determinism_artifacts", "test_execution_results",
  "build_execution_results", "evidence_artifacts", "heldout_access_audit", "certification_gate_summary",
];

export const SENTINELS = new Set(["NOT_CAPTURED", "NOT_RUN", "NULL", "", "null", "undefined", "UNKNOWN"]);
export const isSentinel = (v) => v === null || v === undefined || SENTINELS.has(String(v).trim());
export const PRIMARY_KEY = "id";

export async function loadContract() {
  // API-first with governed bundled fallback. The SQL-backed common API, when configured and
  // reachable, supplies live rows and flips the source to SQL_API; otherwise the app renders
  // instantly and reliably from the governed static fallback bundle.
  try {
    const meta = await axios.get(`${API}/contract/meta`, { timeout: 4000 }).then((r) => r.data);
    if (meta && meta.sql_api_configured) {
      const overview = await axios.get(`${API}/contract/overview`, { timeout: 8000 }).then((r) => r.data);
      return buildModel(meta, overview);
    }
  } catch (_e) {
    /* fall back to governed static bundle */
  }
  const meta = {
    contract_version: bundled.contract_version,
    active_source: "GOVERNED_STATIC_FALLBACK",
    sql_api_configured: false,
    tables: bundled.tables_meta,
    relationships: bundled.relationships,
    counts: bundled.counts,
    companies: bundled.companies,
  };
  return buildModel(meta, bundled.records);
}

const humanize = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/g, "ID");
export { humanize };

function buildModel(meta, records) {
  const relationships = meta.relationships || [];
  const fkByField = new Map();
  relationships.forEach((r) =>
    fkByField.set(`${r.childTable}.${r.childField}`, { referencedTable: r.parentTable, referencedField: r.parentField })
  );
  const parentsOf = {}, childrenOf = {};
  relationships.forEach((r) => {
    (childrenOf[r.parentTable] = childrenOf[r.parentTable] || new Set()).add(r.childTable);
    (parentsOf[r.childTable] = parentsOf[r.childTable] || new Set()).add(r.parentTable);
  });

  const tables = (meta.tables || []).map((t) => {
    const fields = (t.fields || []).map((f) => {
      const fk = fkByField.get(`${t.table}.${f.name}`);
      const isPk = f.name === PRIMARY_KEY;
      return {
        ...f,
        isPrimaryKey: isPk,
        isForeignKey: Boolean(fk),
        referencedTable: fk?.referencedTable,
        referencedField: fk?.referencedField,
        provenance: isPk ? "system-generated" : fk ? "governed foreign key" : "captured/derived",
      };
    });
    return {
      ...t,
      fields,
      rowCount: (records[t.table] || []).length,
      primaryKey: PRIMARY_KEY,
      foreignKeys: fields.filter((f) => f.isForeignKey).map((f) => ({ field: f.name, referencedTable: f.referencedTable, referencedField: f.referencedField })),
      parentTables: Array.from(parentsOf[t.table] || []),
      childTables: Array.from(childrenOf[t.table] || []),
    };
  });

  const enrichedRels = relationships.map((r) => {
    const parents = records[r.parentTable] || [];
    const children = records[r.childTable] || [];
    const parentKeys = new Set(parents.map((p) => p[r.parentField]).filter((v) => !isSentinel(v)).map(String));
    const childVals = children.map((c) => c[r.childField]).filter((v) => !isSentinel(v)).map(String);
    let matched = 0, orphan = 0;
    childVals.forEach((v) => (parentKeys.has(v) ? matched++ : orphan++));
    return {
      ...r,
      mandatory: r.mandatory || "optional",
      joinDirection: r.joinDirection || `${r.parentTable}.${r.parentField} → ${r.childTable}.${r.childField}`,
      parentCount: parents.length,
      childCount: children.length,
      matchedCount: matched,
      orphanCount: orphan,
      integrity: orphan === 0 ? "PASS" : "FAIL",
    };
  });

  const totalFields = tables.reduce((s, t) => s + t.fields.length, 0);
  const fieldDesc = tables.reduce((s, t) => s + t.fields.filter((f) => (f.description || "").trim()).length, 0);
  const orphans = enrichedRels.reduce((s, r) => s + r.orphanCount, 0);

  return {
    activeSource: meta.active_source,
    sqlApiConfigured: meta.sql_api_configured,
    contractVersion: meta.contract_version,
    companies: meta.companies || [],
    counts: meta.counts || {},
    records,
    tables,
    relationships: enrichedRels,
    evidence: {
      tablesDisplayed: tables.length,
      totalFields,
      tableDescriptionCoverage: tables.filter((t) => (t.description || "").trim()).length,
      fieldDescriptionCoverage: fieldDesc,
      primaryKeysDocumented: tables.length,
      foreignKeysDocumented: enrichedRels.length,
      relationshipsDocumented: enrichedRels.length,
      referentialIntegrity: orphans === 0 ? "PASS" : "FAIL",
      orphanRecordCount: orphans,
    },
  };
}

// ---- Balance-sheet reconstruction from governed rows/cells (no fabrication) ----
const NUM_RE = /^[\(\-]?[\d,]+(\.\d+)?\)?$/;
const isNumeric = (t) => NUM_RE.test(String(t || "").trim()) && /\d/.test(String(t));

export function reconstructBalanceSheet(model, reportId, basis) {
  return reconstructStatement(model, reportId, basis, "BALANCE_SHEET");
}

export function reconstructStatement(model, reportId, basis, statement) {
  const rows = (model.records.source_rows || []).filter(
    (r) => r.report_id === reportId && r.statement === statement && (basis === "ALL" || r.basis === basis || isSentinel(r.basis))
  );
  const eligByRow = {};
  (model.records.structural_eligibility || []).forEach((e) => (eligByRow[e.source_row_id] = e));
  const cellsByRow = {};
  (model.records.source_cells || []).forEach((c) => (cellsByRow[c.source_row_id] = cellsByRow[c.source_row_id] || []).push(c));

  return rows
    .map((row) => {
      const cells = (cellsByRow[row.id] || []).slice().sort((a, b) => parseFloat(a.x0 || 0) - parseFloat(b.x0 || 0));
      const labelCells = cells.filter((c) => !isNumeric(c.raw_text));
      const numCells = cells.filter((c) => isNumeric(c.raw_text));
      const label = labelCells.map((c) => c.raw_text).join(" ").trim() || row.raw_text;
      const n = numCells.length;
      return {
        row,
        label,
        page: row.page,
        classification: eligByRow[row.id]?.classification || "UNCLASSIFIED",
        current: n >= 2 ? numCells[n - 2].raw_text : (numCells[0]?.raw_text ?? null),
        comparative: n >= 2 ? numCells[n - 1].raw_text : null,
        cells,
        eligibility: eligByRow[row.id],
      };
    })
    .sort((a, b) => parseFloat(a.row.source_order || 0) - parseFloat(b.row.source_order || 0));
}

export function periodRoles(model, reportId) {
  const ev = (model.records.header_period_evidence || []).filter((h) => h.report_id === reportId && h.statement === "BALANCE_SHEET");
  const cur = ev.find((h) => h.period_role === "CURRENT_PERIOD");
  const cmp = ev.find((h) => h.period_role === "COMPARATIVE_PERIOD");
  return { current: cur, comparative: cmp, all: ev };
}

export function atomsForCell(model, cellId) {
  const edges = (model.records.lineage_edges || []).filter((e) => e.from_id === cellId);
  const atomIds = new Set(edges.map((e) => e.to_id));
  return (model.records.source_atoms || []).filter((a) => atomIds.has(a.id));
}
